import type { CopyGenerationInputSchema } from '@inference.engine/widgets/web';
import type { z } from 'zod';
import { i18n } from '@/shared/i18n';

type CopyGenerationInput = z.infer<typeof CopyGenerationInputSchema>;

// Getters, not values: these arrays are built while the module is evaluated, so resolving per
// read is what lets a label follow a language change instead of freezing at import.
export const SUPPORTED_CHANNELS = [
  {
    get label() {
      return i18n.t('chats:chats.copyGenerationOptions.channelPlainText');
    },
    // eslint-disable-next-line i18next/no-literal-string -- channel option wire value
    value: 'plain',
  },
  /* eslint-disable i18next/no-literal-string -- social platform names */
  { label: 'Instagram', value: 'instagram' },
  { label: 'TikTok', value: 'tiktok' },
  { label: 'X (Twitter)', value: 'twitter' },
  { label: 'Facebook', value: 'facebook' },
  /* eslint-enable i18next/no-literal-string */
  {
    get label() {
      return i18n.t('chats:chats.copyGenerationOptions.channelEmail');
    },
    // eslint-disable-next-line i18next/no-literal-string -- channel option wire value
    value: 'email',
  },
] as const;

/* eslint-disable i18next/no-literal-string -- response style option wire values */
export const SUPPORTED_RESPONSE_STYLE = [
  {
    get label() {
      return i18n.t('chats:chats.copyGenerationOptions.responseStyleFocused');
    },
    value: 'focused',
  },
  {
    get label() {
      return i18n.t('chats:chats.copyGenerationOptions.responseStyleBalanced');
    },
    value: 'balanced',
  },
  {
    get label() {
      return i18n.t('chats:chats.copyGenerationOptions.responseStyleImaginative');
    },
    value: 'imaginative',
  },
] as const;
/* eslint-enable i18next/no-literal-string */

export const SUPPORTED_VARIATIONS = [
  { label: '1x', value: 1 },
  { label: '2x', value: 2 },
  { label: '3x', value: 3 },
  { label: '4x', value: 4 },
  { label: '5x', value: 5 },
  { label: '6x', value: 6 },
  { label: '7x', value: 7 },
  { label: '8x', value: 8 },
  { label: '9x', value: 9 },
  { label: '10x', value: 10 },
] as const;

export type Channel = (typeof SUPPORTED_CHANNELS)[number]['value'];
export type ResponseStyle = (typeof SUPPORTED_RESPONSE_STYLE)[number]['value'];
export type Variations = (typeof SUPPORTED_VARIATIONS)[number]['value'];

/**
 * Every key that travels to the agent as its `input`, named against the agent's own schema.
 *
 * Named here rather than hand-declared because the schema drops any key it does not declare, so a
 * name that drifts is not an error anywhere: the value is simply never seen by a model. `Pick`
 * constrains its keys to `keyof CopyGenerationInput`, so a rename fails `tsc` instead.
 */
type WireKeys = Pick<CopyGenerationInput, 'goals' | 'enableQAFeedback' | 'channels' | 'variations'>;

/**
 * `variations` narrowed to the steps this popup offers, a subset of the 1-10 the schema accepts.
 * `Omit` rather than dropping the key from the `Pick`, so the name stays checked against the schema.
 */
type SchemaBackedOptions = Omit<WireKeys, 'variations'> & {
  variations: Variations;
};

/**
 * Fields that never reach the agent's input: they configure the call instead.
 *
 * `model` is owned by the popup only under V1, which renders the model dropdown. Under V2 the
 * toolbar chip is the control the user sees, and it writes `llmOptions.model`.
 */
type UiOnlyOptions = {
  model: string;
  responseStyle: ResponseStyle;
  knowledgeBaseIds?: string[];
};

export type CopyOptions = SchemaBackedOptions & UiOnlyOptions;
